import React, { useEffect, useState } from 'react';
import { DateRangePicker, DatePicker } from 'rsuite';
import { toISODate, toMidpointISO } from '../../../utils/dates';
import MonthSelector from './MonthSelector';
import 'rsuite/dist/rsuite.min.css';

// ---- Helpers (modo local, consistentes con tu código) ----
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);

// Lunes→Domingo para la semana que contiene "date" (basado en locale, no UTC)
const getIsoWeekRangeLocal = (date = new Date()) => {
  const base = startOfDay(date);
  // mapear: 0=Dom,1=Lun,... -> 0=Lun,...,6=Dom
  const dowMon0 = base.getDay();
  const start = new Date(base);
  start.setDate(base.getDate() - dowMon0); // lunes
  const end = endOfDay(new Date(start));
  end.setDate(start.getDate() + 6); // domingo
  return [start, end];
};

const getMonthRange = (date = new Date()) => {
  const start = new Date(date.getFullYear(), date.getMonth(), 1);
  const end = endOfDay(new Date(date.getFullYear(), date.getMonth() + 1, 0));
  return [start, end];
};

/**
 * FlexibleDateSelector
 * A flexible date selection component supporting different modes: 'day', 'week', or 'month'.
 *
 * Props:
 * - mode: 'day' | 'week' | 'month' — determines which date picker UI is shown
 */
function FlexibleDateSelector({ mode = 'day', onChange }) {
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedRange, setSelectedRange] = useState(null);
  const [formattedDate, setFormattedDate] = useState(null);

  // Set default según mode
  useEffect(() => {
    const today = new Date();
    if (mode === 'day') {
      setSelectedRange(null);
      setSelectedDay(today);
    } else if (mode === 'week') {
      setSelectedDay(null);
      setSelectedRange(getIsoWeekRangeLocal(today)); // lunes→domingo
    } else if (mode === 'month') {
      setSelectedDay(null);
      setSelectedRange(getMonthRange(today));
    }
  }, [mode]);

  // Formatea lo que se envía a la API (string ISO yyyy-mm-dd)
  useEffect(() => {
    let formatedSelectedDate;
    if (selectedDay) formatedSelectedDate = toISODate(selectedDay);
    if (selectedRange) formatedSelectedDate = toMidpointISO(selectedRange);
    setFormattedDate(formatedSelectedDate || null);
  }, [selectedDay, selectedRange]);

  useEffect(() => {
    if (formattedDate) onChange?.(formattedDate, selectedDay || selectedRange);
  }, [formattedDate, onChange, selectedDay, selectedRange]);

  return (
    <div style={{ maxWidth: 400 }}>
      {mode === 'month' && (
        <div className="w-full !w-[225px]">
          <MonthSelector value={selectedRange} onChange={setSelectedRange} />
        </div>
      )}

      {mode === 'day' && (
        <div className="w-full !w-[225px]">
          <DatePicker
            oneTap
            value={selectedDay}
            onChange={setSelectedDay}
            format="MM/dd/yyyy"
            placeholder="Select a day"
            className="w-full"
            caretAs={null}
          />
        </div>
      )}

      {mode === 'week' && (
        <div className="w-full !w-[225px]">
          <DateRangePicker
            showOneCalendar
            oneTap
            isoWeek // <-- la semana empieza en lunes en el calendario
            hoverRange={getIsoWeekRangeLocal} // <-- al pasar el mouse/click, marca Lun→Dom
            value={selectedRange}
            onChange={(range) => {
              if (!range) {
                setSelectedRange(null);
                return;
              }
              // Normalize SIEMPRE a lunes→domingo según el primer día clickeado
              const normalized = getIsoWeekRangeLocal(range[0] || new Date());
              setSelectedRange(normalized);
            }}
            format="MM/dd/yyyy"
            placeholder="Select a week"
            className="w-full"
            caretAs={null}
          />
        </div>
      )}
    </div>
  );
}

export default FlexibleDateSelector;
